import { type DecorationFontFamily, fontFamilyKey } from "@innoclouds/ui";
import { shallowRef } from "vue";
import { loadFace, removeFaces } from "./faces";
import { applyFontFit, FONT_FIT_VAR_PREFIX, type FontFitRatios, measureFit, resetFontFit } from "./fit";
import { readWeightAxis, weightRange } from "./fontFile";

export type NamedFontResult = {
	family: string;
	status: "loaded" | "failed";
	ratios?: FontFitRatios;
};

type NamedFontFile = { url: string; weight: number; style: "normal" | "italic" };

type NamedFontRun = {
	name: string;
	family: string;
	signature: string;
	prefix: string;
	faces: FontFace[];
	ratios: FontFitRatios | null;
	pending: boolean;
	settled: Promise<void>;
};

const DEFAULT_WEIGHT = 400;
const NO_FAMILIES: readonly string[] = Object.freeze([]);

const runs = new Map<string, NamedFontRun>();

export const namedFontFamilies = shallowRef<readonly string[]>(NO_FAMILIES);

const nameOf = (family: string) => family.trim().toLowerCase();

const filesOf = ({ files }: DecorationFontFamily): NamedFontFile[] =>
	files.map(({ url, weight, style }) => ({ url, weight: weight ?? DEFAULT_WEIGHT, style: style ?? "normal" }));

const signatureOf = (family: DecorationFontFamily) => JSON.stringify([family.family, filesOf(family)]);

const isCurrent = (run: NamedFontRun) => runs.get(run.name) === run;

const isReusable = (run: NamedFontRun, family: DecorationFontFamily) => run.signature === signatureOf(family) && (run.pending || run.faces.length > 0);

const drop = (run: NamedFontRun) => {
	if (isCurrent(run)) runs.delete(run.name);
	removeFaces(run.faces);
	run.faces = [];
	run.ratios = null;
	resetFontFit(run.prefix);
};

const adoptFace = async (run: NamedFontRun, { url, weight, style }: NamedFontFile) => {
	const axis = await readWeightAxis(url);
	const face = await loadFace(run.family, url, { weight: axis ? weightRange(axis) : String(weight), style }).catch(() => null);
	if (!face || !isCurrent(run)) return;
	document.fonts.add(face);
	run.faces.push(face);
};

const loadRun = async (run: NamedFontRun, files: NamedFontFile[]) => {
	await Promise.all(files.map((file) => adoptFace(run, file)));
	if (!run.faces.length || !isCurrent(run)) return;
	const report = await measureFit(run.family);
	if (!report || !isCurrent(run)) return;
	run.ratios = report.ratios;
	applyFontFit(report.ratios, run.prefix);
};

const startRun = (name: string, family: DecorationFontFamily) => {
	const run: NamedFontRun = {
		name,
		family: family.family,
		signature: signatureOf(family),
		prefix: `${FONT_FIT_VAR_PREFIX}-f-${fontFamilyKey(family.family)}`,
		faces: [],
		ratios: null,
		pending: true,
		settled: Promise.resolve(),
	};
	runs.set(name, run);
	run.settled = loadRun(run, filesOf(family))
		.catch(() => {})
		.finally(() => {
			run.pending = false;
		});
	return run;
};

const publish = (families: string[]) => {
	const known = namedFontFamilies.value;
	if (families.length === known.length && families.every((family, index) => family === known[index])) return;
	namedFontFamilies.value = families.length ? Object.freeze(families) : NO_FAMILIES;
};

const resultOf = ({ family, faces, ratios }: NamedFontRun): NamedFontResult => ({
	family,
	status: faces.length ? "loaded" : "failed",
	...(ratios && { ratios: { ...ratios } }),
});

export const syncNamedFonts = (families: readonly DecorationFontFamily[]): Promise<NamedFontResult[]> => {
	const wanted = new Map(families.map((family) => [nameOf(family.family), family]));
	for (const run of [...runs.values()]) {
		const family = wanted.get(run.name);
		if (!family || !isReusable(run, family)) drop(run);
	}
	const current = [...wanted].map(([name, family]) => runs.get(name) ?? startRun(name, family));
	publish(current.map((run) => run.family));
	return Promise.all(current.map((run) => run.settled)).then(() => current.map(resultOf));
};
